import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_PAYMENTS,
  RECEIVE_ALL_PAYMENTS,
  RECEIVING_PAYMENTS,
  RECEIVE_PAYMENT_METADATA,
  INCREMENT_PAYMENT_COUNT,
  RECEIVE_PAYMENT_FAILURE,
  SAVING_PAYMENT,
  SAVING_PAYMENT_FAILURE,
  UPDATING_PAYMENT,
  UPDATING_PAYMENT_FAILURE,
  DELETE_PAYMENT,
  DELETING_PAYMENT,
  DELETING_PAYMENT_FAILURE,
  CLEAR_PAYMENT_STATE,
  CLEAR_STATE,
  CLEAR_CACHE,
  UPDATE_CURRENT_PAYMENT_PAGE,
  RESET_PAYMENT_META
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  receiveMetadata,
  deleteData,
  increment,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const paymentSchema = new schema.Entity('payments')
const paymentListSchema = new schema.Array(paymentSchema)

const state = {
  data: {},
  all: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: false,
  deleting: false,
  deleteFailure: null,
  selection: []
}

const actions = {
  fetchPaymentsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PAYMENTS, true)
    return Vue.http.get(buildUrlWithQuery('payments', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_PAYMENTS, false)
        this.commit(RESET_PAYMENT_META)
        if (!(r && r.results)) return this.commit(RECEIVE_PAYMENT_FAILURE, 'Error fetching payments')
        const { results, next, previous, count } = r
        const pentity = new schema.Entity('payments', undefined, {
          idAttribute: (value) => value.ledger || value.id
        })
        const pschema = new schema.Array(pentity)
        const { entities, result } = normalize(results, pschema)
        if (!result || !Array.isArray(result)) return this.commit(RECEIVE_PAYMENT_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_PAYMENTS, { data: entities.payments })
        this.commit(RECEIVE_PAYMENT_METADATA, { next, previous, count, result: result.length })
        this.commit(UPDATE_CURRENT_PAYMENT_PAGE, query && query.page || 1)
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_PAYMENTS, false)
        this.commit(RECEIVE_PAYMENT_FAILURE, handleXHRError(e))
      })
  },
  async fetchAllPaymentsWithQuery (store, query, pageSize = 200) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (!query.page_size) query.page_size = pageSize
    try {
      this.commit(RECEIVING_PAYMENTS, true)
      const url = buildUrlWithQuery('payments', query, instanceUrl)
      const firstPage = await Vue.http.get(url, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!(firstPage && firstPage.results)) throw new Error(`Error fetching all payments`)
      const requests = []
      if (firstPage.count > pageSize) {
        const pageCt = Math.ceil(firstPage.count / pageSize)
        for (let i = 2; i <= pageCt; i++) {
          requests.push(Vue.http.get(`${url}&page=${i}`, { headers: getHeaders(this.getters.getToken) })
            .then(checkStatus)
            .then(parseJSON)
          )
        }
      }
      const nextPages = await Promise.all(requests)
      let allResults = firstPage.results
      if (nextPages && nextPages.length) {
        allResults = [...allResults, ...nextPages.reduce((a, b) => a.concat(b.results), [])]
      }
      this.commit(RECEIVING_PAYMENTS, false)
      const { entities } = normalize(allResults, paymentListSchema)
      for (const i in entities.payments) {
        entities.payments[i].type = 'payments'
      }
      this.commit(RECEIVE_PAYMENTS, { data: entities.payments })
      return { query, count: firstPage.count, entities }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVING_PAYMENTS, false)
      return e
    }
  },
  fetchAllPayments (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PAYMENTS, true)
    return Vue.http.get(buildUrl('payments?serialize=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        this.commit(RECEIVING_PAYMENTS, false)
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_PAYMENT_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_PAYMENTS, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVING_PAYMENTS, false)
        this.commit(RECEIVE_PAYMENT_FAILURE, e.statusText)
      })
  },
  updatePayment (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_PAYMENT, id)
    this.commit(UPDATING_PAYMENT_FAILURE, { id: '', err: '' })
    return Vue.http.patch(buildUrl(`payments/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_PAYMENT, null)
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, paymentSchema)
          if (!result || (Array.isArray(result) && !result.length)) return this.commit(RECEIVE_PAYMENT_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_PAYMENTS, { data: entities.payments })
          return r.body
        } else {
          this.commit(RECEIVE_PAYMENT_FAILURE, r ? r.statusText : 'Failed to update Payment')
        }
      })
      .catch(e => {
        this.commit(UPDATING_PAYMENT, null)
        this.commit(UPDATING_PAYMENT_FAILURE, { id, err: handleXHRError(e) })
      })
  },
  voidPayment (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_PAYMENT, id)
    this.commit(UPDATING_PAYMENT_FAILURE, { id: '', err: '' })
    return Vue.http.post(buildUrl(`payments/${id}/void`, null, instanceUrl), data, { headers })
      .then(r => {
        return r.body
      })
      .catch(e => {
        this.commit(UPDATING_PAYMENT, null)
        this.commit(UPDATING_PAYMENT_FAILURE, { id, err: handleXHRError(e) })
      })
  },
  deletePayment (store, payload) {
    const { payment_ids, ledger_ids } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    payment_ids.forEach(element => {
      this.commit(UPDATING_PAYMENT, element)
    })
    this.commit(UPDATING_PAYMENT_FAILURE, { id: '', err: '' })
    return Vue.http.post(buildUrl(`payments/bulk_delete`, null, instanceUrl), { payment_ids, ledger_ids }, { headers })
      .then(r => {
        return r.body
      })
      .catch(e => {
        this.commit(UPDATING_PAYMENT, null)
        this.commit(UPDATING_PAYMENT_FAILURE, { payment_ids, err: handleXHRError(e) })
        return e
      })
  },
  clearPaymentState (store, clearCache = false) {
    this.commit(CLEAR_PAYMENT_STATE, clearCache)
    return true
  },
  resetPaymentMeta () {
    this.commit(RESET_PAYMENT_META)
  },
  notifyPayment (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.post(buildUrl('notify/payments', null, instanceUrl), payload, { headers })
      .then(r => {
        return r.body
      })
      .catch(e => {
        this.commit(UPDATING_PAYMENT, null)
        this.commit(UPDATING_PAYMENT_FAILURE, { id: null, err: handleXHRError(e) })
      })
  },
  updateSelection ({ commit }, selectionData) {
    commit('SET_SELECTION', selectionData)
  }
}

const getters = {
  getSelection: (state) => state.selection,
  getPayments: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getPaymentById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllPayments: (state) => {
    return state.all
  },
  getAllCachedPayments: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getPaymentMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getPaymentFetchStatus: (state) => {
    return state.receivingData
  },
  getPaymentByRelatedTransactionId: (state) => (id) => {
    return state.data ? Object.values(state.data).filter(v => v.related_transactions && v.related_transactions.includes(parseInt(id))) : []
  },
  getPaymentSaveStatus: (state) => {
    return state.saving
  },
  getPaymentUpdateStatus: (state) => {
    return state.updating
  },
  getPaymentDeleteStatus: (state) => {
    return state.deleting
  },
  getCurrentPaymentPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  SET_SELECTION (state, payload) {
    state.selection = payload
  },
  [RECEIVE_PAYMENTS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVE_ALL_PAYMENTS] (state, results) {
    const { data } = results
    state.all = data
  },
  [RECEIVING_PAYMENTS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_PAYMENT_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_PAYMENT] (state, payload) {
    state.saving = false
  },
  [SAVING_PAYMENT_FAILURE] (state, payload) {
    state.saveFailure = null
  },
  [UPDATING_PAYMENT] (state, payload) {
    state.updating = payload
  },
  [UPDATING_PAYMENT_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_PAYMENT] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_PAYMENT] (state, payload) {
    state.deleting = payload
  },
  [DELETING_PAYMENT_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleting = { id, err }
  },
  [INCREMENT_PAYMENT_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_PAYMENT_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [UPDATE_CURRENT_PAYMENT_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_PAYMENT_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_PAYMENT_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  [CLEAR_CACHE] (state) {
    state.cache = Object.assign({})
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/payment.js